import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

const CHAT_BASE = environment.apiUrl;

/** צ'אט ללא הזמנה – מפתח מערכת (תואם בקשות ל־Back) */
export type ChatProductSystemKey = 'phPrint' | '3DPrint';

export type ChatAttachmentKind = 'image' | 'file';

/** מצורף לצ'אט (תמונה או קובץ) – נשמר בהזמנה; readAt/isRead לצד שלא העלה */
export interface ChatMediaAttachment {
  _id?: string;
  /** מזהה קובץ בשרת / ב-Express (כמו אחרי upload3dprintfile) */
  fileId: string;
  /** שם קובץ לתצוגה והורדה */
  fileName?: string;
  /** שם קובץ תמונה ממוזערת בתיקיית uploads של המשתמש (אופציונלי) */
  thumbnailPath?: string;
  /** URL מלא לתצוגה – אם השרת שולח */
  previewUrl?: string;
  /** URL להורדה – אם השרת שולח */
  downloadUrl?: string;
  attachedAt: string;
  senderType: 'user' | 'manager';
  isRead?: boolean;
  readAt?: string | null;
}

export interface ChatMessage {
  _id?: string;
  content: string;
  senderType: 'user' | 'manager';
  createdAt: string;
  isRead?: boolean;
  readAt?: string | null;
}

export interface OrderWithChat {
  order: any;
  messages: ChatMessage[];
  priceQuotes?: PriceQuoteInChat[];
  /** מצב צ'אט ללא הזמנה – מצורפים ברמת שורש אם אין `order` */
  chatAttachedImages?: ChatMediaAttachment[];
  chatAttachedFiles?: ChatMediaAttachment[];
  /** צ'אט ללא הזמנה – פרטי לקוח לכותרת מנהל (מילוי ב־Back) */
  customerEmail?: string | null;
  customerPhone?: string | null;
}

/** שורת סיכום של שיחת-מערכת (ללא הזמנה) – ברשימת ניהול המנהל */
export interface NoOrderChatSummary {
  _id: string;
  userId: string;
  chatProductSystem: ChatProductSystemKey;
  createdAt: string;
  updatedAt: string;
  managerMessageCount: number;
  userMessageCount: number;
  /** מייל המשתמש – ממולא ב-Back */
  userEmail?: string | null;
  /** טלפון המשתמש – ממולא ב-Back */
  userPhone?: string | null;
}

/** הצעת מחיר כפי שנשמרה בצ'אט (מהשרת) */
export interface PriceQuoteInChat {
  _id?: string;
  orderId: string;
  orderPrice: number;
  orderNote?: string;
  files: { _id: string; price: number; note?: string }[];
  createdAt: string;
  /** מועד התחייבות – רק בהזמנות PH */
  obligatedDay?: string;
  obligatedDate?: string;
  obligatedTime?: string;
  /** עלות שילוח/איסוף שערך המנהל בהצעת המחיר */
  selectedCollectPriceModified?: number;
  /** נפתח ע"י המשתמש – מוצג V למנהל */
  isRead?: boolean;
  readAt?: string | null;
  /** בוטלה ע"י המנהל */
  isCancelled?: boolean;
  cancelledAt?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ChatService {
  constructor(private http: HttpClient) {}

  getOrderWithChatForUser(userId: string, orderId: string, readAt?: string): Observable<OrderWithChat> {
    const params = readAt ? { readAt } : {};
    return this.http.get<OrderWithChat>(`${CHAT_BASE}/chat/user/${userId}/${orderId}`, { params });
  }

  /** צ'אט משתמש לפי מערכת בלי הזמנה (ייווצר ב־Back אם חסר) */
  getUserSystemChat(userId: string, system: ChatProductSystemKey, readAt?: string): Observable<OrderWithChat> {
    const params = readAt ? { readAt } : {};
    return this.http.get<OrderWithChat>(`${CHAT_BASE}/chat/user/${userId}/system/${system}`, { params });
  }

  sendMessageAsUser(userId: string, orderId: string, content: string): Observable<OrderWithChat> {
    return this.http.post<OrderWithChat>(`${CHAT_BASE}/chat/user/${userId}/${orderId}`, {
      content,
    });
  }

  sendMessageAsUserSystemChat(userId: string, system: ChatProductSystemKey, content: string): Observable<OrderWithChat> {
    return this.http.post<OrderWithChat>(`${CHAT_BASE}/chat/user/${userId}/system/${system}`, {
      content,
    });
  }

  getOrderWithChatForManager(orderId: string, readAt?: string): Observable<OrderWithChat> {
    const params = readAt ? { readAt } : {};
    return this.http.get<OrderWithChat>(`${CHAT_BASE}/chat/manager/${orderId}`, { params });
  }

  sendMessageAsManager(orderId: string, content: string): Observable<OrderWithChat> {
    return this.http.post<OrderWithChat>(`${CHAT_BASE}/chat/manager/${orderId}`, {
      content,
    });
  }

  /**
   * אחרי העלאת קובץ ל-upload3dprintfile – רישום המצורף במערך תמונות/קבצים של הצ'אט בהזמנה.
   * אם השרת מחזיר OrderWithChat כבר בתשובת ההעלאה – אין חובה לקרוא לזה.
   */
  registerChatAttachment(
    isManager: boolean,
    userId: string,
    orderId: string,
    body: { fileId: string; kind: ChatAttachmentKind }
  ): Observable<OrderWithChat> {
    if (isManager) {
      return this.http.post<OrderWithChat>(`${CHAT_BASE}/chat/manager/${orderId}/attachment`, body);
    }
    return this.http.post<OrderWithChat>(`${CHAT_BASE}/chat/user/${userId}/${orderId}/attachment`, body);
  }

  registerSystemChatAttachment(userId: string, system: ChatProductSystemKey, body: { fileId: string; kind: ChatAttachmentKind }): Observable<OrderWithChat> {
    return this.http.post<OrderWithChat>(`${CHAT_BASE}/chat/user/${userId}/system/${system}/attachment`, body);
  }

  /** צ'אט מנהל לפי מערכת בלי הזמנה — userId בנתיב הוא הלקוח (לא המנהל המחובר) */
  getManagerSystemChat(customerUserId: string, system: ChatProductSystemKey, readAt?: string): Observable<OrderWithChat> {
    const params = readAt ? { readAt } : {};
    return this.http.get<OrderWithChat>(`${CHAT_BASE}/chat/manager/${customerUserId}/system/${system}`, { params });
  }

  sendMessageAsManagerSystemChat(customerUserId: string, system: ChatProductSystemKey, content: string): Observable<OrderWithChat> {
    return this.http.post<OrderWithChat>(`${CHAT_BASE}/chat/manager/${customerUserId}/system/${system}`, {
      content,
    });
  }

  registerManagerSystemAttachment(
    customerUserId: string,
    system: ChatProductSystemKey,
    body: { fileId: string; kind: ChatAttachmentKind }
  ): Observable<OrderWithChat> {
    return this.http.post<OrderWithChat>(`${CHAT_BASE}/chat/manager/${customerUserId}/system/${system}/attachment`, body);
  }

  /**
   * רשימת שיחות-מערכת (ללא הזמנה) למנהל, עם פגינציה.
   * GET /chat/manager/system-chats?system=3DPrint&pagesize=10&page=1
   */
  getManagerNoOrderChats(
    system: ChatProductSystemKey,
    chatsPerPage: number,
    currentPage: number,
  ): Observable<{ chats: NoOrderChatSummary[]; totalChats: number }> {
    const params = `?system=${system}&pagesize=${chatsPerPage}&page=${currentPage}`;
    return this.http.get<{ chats: NoOrderChatSummary[]; totalChats: number }>(
      `${CHAT_BASE}/chat/manager/system-chats${params}`,
    );
  }

  /** שליחת הצעת מחיר – POST לראוט chat/price-quote */
  sendPriceQuote(payload: PriceQuotePayload): Observable<PriceQuoteResponse> {
    return this.http.post<PriceQuoteResponse>(`${CHAT_BASE}/chat/price-quote`, payload);
  }

  /** ביטול הצעת מחיר – POST לראוט chat/price-quote/cancel */
  cancelPriceQuote(orderId: string, priceQuoteIndex: number): Observable<OrderWithChat> {
    return this.http.post<OrderWithChat>(`${CHAT_BASE}/chat/price-quote/cancel`, { orderId, priceQuoteIndex });
  }

  /** תשלום על הזמנת PH (מהצעת מחיר) – POST לראוט chat/price-quote/pay */
  payPHOrder(orderId: string, priceQuoteIndex: number, creditCardInfo?: {
    exp_m: string; exp_y: string; num: string; cvv: string; id: string; name: string;
    customerName?: string; customerEmail?: string; customerBusinessID?: string;
  }): Observable<OrderWithChat> {
    const body: any = { orderId, priceQuoteIndex };
    if (creditCardInfo) body.creditCardInfo = creditCardInfo;
    return this.http.post<OrderWithChat>(`${CHAT_BASE}/chat/price-quote/pay`, body);
  }

  /** התחלת ייצור הזמנת PH – PUT לראוט chat/ph/order/:orderId/start-production. מעדכן סטטוס ל-STARTED_PRODUCTION, שומר productionStartedAt ו-productionStartedNote, שולח מייל ו-SMS */
  markProductionStarted(orderId: string, note?: string): Observable<OrderWithChat> {
    const body: { note?: string } = {};
    if (note?.trim()) body.note = note.trim();
    return this.http.put<OrderWithChat>(`${CHAT_BASE}/chat/ph/order/${orderId}/start-production`, body);
  }

  /** סיום ייצור הזמנת PH – PUT לראוט chat/ph/order/:orderId/finish-production. מעדכן סטטוס ל-FINISHED_PRODUCTION, שומר productionFinishedAt ו-productionFinishedNote */
  markProductionFinished(orderId: string, note?: string): Observable<OrderWithChat> {
    const body: { note?: string } = {};
    if (note?.trim()) body.note = note.trim();
    return this.http.put<OrderWithChat>(`${CHAT_BASE}/chat/ph/order/${orderId}/finish-production`, body);
  }

  /** סימון שההזמנה הגיעה לכתובת – PUT לראוט chat/ph/order/:orderId/arrived-at-address. מעדכן סטטוס ל-FINISHED_SHIPMENT, שומר shipmentFinishedAt ו-shipmentFinishedNote */
  markOrderArrivedAtAddress(orderId: string, note?: string): Observable<OrderWithChat> {
    const body: { note?: string } = {};
    if (note?.trim()) body.note = note.trim();
    return this.http.put<OrderWithChat>(`${CHAT_BASE}/chat/ph/order/${orderId}/arrived-at-address`, body);
  }

  /** סימון שהלקוח אסף את ההזמנה – PUT לראוט chat/user/:userId/order/:orderId/collected. userId = המשתמש המחובר (להתאמה ל-checkAuthPrivate). */
  markOrderCollected(userId: string, orderId: string): Observable<OrderWithChat> {
    return this.http.put<OrderWithChat>(`${CHAT_BASE}/chat/user/${userId}/order/${orderId}/collected`, {});
  }
}

export interface PriceQuoteFileItem {
  _id: string;
  price: number;
  note?: string;
}

export interface PriceQuotePayload {
  orderId: string;
  orderPrice: number;
  orderNote?: string;
  files: PriceQuoteFileItem[];
  /** מועד התחייבות – רק בהזמנות PH */
  obligatedDay?: string;
  obligatedDate?: string;
  obligatedTime?: string;
  /** עלות שילוח/איסוף שערך המנהל – רק כשיש נקודות איסוף או משלוח עד הבית; נשמר כ-selectedCollectPriceModified */
  selectedCollectPriceModified?: number;
}

export interface PriceQuoteResponse {
  success: boolean;
  message?: string;
}
